import { useTheme } from '../../hooks/useTheme'
import { Switch } from './Switch'

interface SettingsToggleRowProps {
  label: string
  description?: string
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  /** Optional id for the label element (for aria-labelledby on Switch) */
  id?: string
  /** Set to true for the last row in a group to remove bottom border */
  last?: boolean
}

/**
 * Standard row for a settings screen: label (optional description) + toggle.
 * Uses theme colors for borders. Use inside Card or a list.
 */
export function SettingsToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  id,
  last = false,
}: SettingsToggleRowProps) {
  const theme = useTheme()
  const labelId = id ?? `toggle-${label.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <label
      id={labelId}
      className={`flex items-center gap-4 py-3 cursor-pointer min-h-[44px] ${last ? 'border-b-0' : ''}`}
      style={
        last
          ? undefined
          : { borderBottom: `1px solid ${theme.cardBorder}` }
      }
    >
      <div className="flex-1 min-w-0">
        <span className="font-medium" style={{ color: theme.text.primary }}>
          {label}
        </span>
        {description && (
          <p
            className="text-sm mt-0.5"
            style={{ color: theme.text.muted }}
          >
            {description}
          </p>
        )}
      </div>
      <div className="flex-shrink-0">
        <Switch
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          aria-labelledby={labelId}
        />
      </div>
    </label>
  )
}
